'use client'

import { useEffect, useState, useSyncExternalStore } from 'react'

/**
 * Whether a phone in this tab is on a call. Published outside React so the
 * desktop can stay up for a live call before it knows anything else: losing
 * the agent role also removes service-center:calls:handle, and the call must
 * outlive both. Each Device hook reports for itself, so one instance mounting
 * or unmounting never clears another's call.
 *
 * Kept free of the phone's own imports: the root layout reads it to keep the
 * lazily loaded phone mounted through a call.
 */
const liveCall = {
  owners: new Set<object>(),
  listeners: new Set<() => void>(),
  report(owner: object, live: boolean) {
    const before = liveCall.owners.size > 0
    if (live) liveCall.owners.add(owner)
    else liveCall.owners.delete(owner)
    if (before === liveCall.owners.size > 0) return
    for (const listener of liveCall.listeners) listener()
  },
  subscribe(listener: () => void) {
    liveCall.listeners.add(listener)
    return () => liveCall.listeners.delete(listener)
  }
}

/** Reports whether `owner`'s phone is on a call (ringing included). */
function reportPhoneCallLive(owner: object, live: boolean): void {
  liveCall.report(owner, live)
}

/** Reports `live` for the calling hook's own phone while it is mounted. */
export function useReportPhoneCallLive(live: boolean): void {
  const [owner] = useState(() => ({}))
  useEffect(() => {
    reportPhoneCallLive(owner, live)
  }, [owner, live])
  useEffect(() => () => reportPhoneCallLive(owner, false), [owner])
}

export function usePhoneCallLive(): boolean {
  return useSyncExternalStore(
    liveCall.subscribe,
    () => liveCall.owners.size > 0,
    () => false
  )
}
